/**
 * Fill patterns for confidence (PRD 7.3, Appendix A): high is solid, medium a 45-degree hatch,
 * low a dot field, each with its marker. Pattern, not opacity and not colour alone, so the tier
 * survives a greyscale print and a colour-vision deficiency.
 *
 * The defs are generated once per document, one pattern per type slot per tier, because an SVG
 * `<pattern>` resolves `currentColor` against itself and not against the shape that references
 * it. Every colour in the generated markup is a custom property, so the same defs redraw
 * correctly when the theme changes — nothing here has to be re-rendered on a theme toggle.
 */
import { TYPE_SLOTS, OTHER_SLOT } from './palette.js';

/** Every slot the defs cover, in order: the seven type hues, then Other. */
export const PATTERN_SLOTS = Object.freeze([...TYPE_SLOTS, OTHER_SLOT]);

/** The three fills, in confidence order. `solid` needs no pattern, it is the colour itself. */
export const FILLS = Object.freeze(['solid', 'hatch', 'dots']);

const HATCH_SIZE = 6;
const DOT_SIZE = 6;

/** The id of the pattern for one slot and fill; `solid` has none. */
export function patternId(fill, slotVar) {
  if (fill === 'solid') return null;
  return `waag-${fill}-${String(slotVar).replace(/^--/, '')}`;
}

/**
 * The paint for a mark: a plain colour for high confidence, a pattern reference otherwise.
 * @param {string} slotVar  a type slot custom property, e.g. `--type-3`
 * @param {'solid'|'hatch'|'dots'} fill
 */
export function fillFor(slotVar, fill = 'solid') {
  const id = patternId(fill, slotVar);
  return id ? `url(#${id})` : `var(${slotVar})`;
}

/** The fill a confidence tier asks for. Unknown tiers draw solid rather than disappearing. */
export function fillForTier(tier) {
  if (tier === 'medium') return 'hatch';
  if (tier === 'low') return 'dots';
  return 'solid';
}

function hatchPattern(slotVar) {
  const id = patternId('hatch', slotVar);
  // Two strokes a half-tile apart, drawn diagonally across a tile that repeats seamlessly.
  return (
    `<pattern id="${id}" width="${HATCH_SIZE}" height="${HATCH_SIZE}" patternUnits="userSpaceOnUse" ` +
    `patternTransform="rotate(45)">` +
    `<rect width="${HATCH_SIZE}" height="${HATCH_SIZE}" fill="var(${slotVar})"/>` +
    `<line x1="0" y1="0" x2="0" y2="${HATCH_SIZE}" stroke="var(--hatch-ink)" stroke-width="2"/>` +
    `</pattern>`
  );
}

function dotsPattern(slotVar) {
  const id = patternId('dots', slotVar);
  return (
    `<pattern id="${id}" width="${DOT_SIZE}" height="${DOT_SIZE}" patternUnits="userSpaceOnUse">` +
    `<rect width="${DOT_SIZE}" height="${DOT_SIZE}" fill="var(${slotVar})"/>` +
    `<circle cx="${DOT_SIZE / 2}" cy="${DOT_SIZE / 2}" r="1.35" fill="var(--dot-ink)"/>` +
    `</pattern>`
  );
}

/**
 * Every pattern the app can reference, as one `<defs>` block. Render it once inside the SVG that
 * uses it (patterns do not cross SVG documents, so each chart carries its own copy).
 */
export function patternDefs() {
  const patterns = PATTERN_SLOTS.flatMap((slot) => [hatchPattern(slot.var), dotsPattern(slot.var)]);
  return `<defs>${patterns.join('')}</defs>`;
}

/**
 * A standalone swatch, for the legend and the style guide: a rounded square filled the way a
 * mark of this slot and tier would be.
 * @param {{ slotVar?: string, fill?: string, size?: number, border?: string }} opts
 */
export function swatchSvg({
  slotVar = '--type-1',
  fill = 'solid',
  size = 16,
  border = 'solid',
} = {}) {
  const dash = border === 'dashed' ? ' stroke-dasharray="3 2"' : '';
  return (
    `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" aria-hidden="true">` +
    patternDefs() +
    `<rect x="0.75" y="0.75" width="${size - 1.5}" height="${size - 1.5}" rx="3" ` +
    `fill="${fillFor(slotVar, fill)}" stroke="var(${slotVar})" stroke-width="1.5"${dash}/>` +
    `</svg>`
  );
}
